import type { ParseComissaoFileResult } from './financeiroComissoesImport'
import type {
  ComissaoParseWorkerRequest,
  ComissaoParseWorkerResponse,
} from '../workers/comissaoParse.worker'

type Pending = {
  resolve: (result: ParseComissaoFileResult) => void
  reject: (error: Error) => void
}

/**
 * Cliente de um Web Worker dedicado ao parse de Excel/CSV de comissões.
 * Um worker por sessão de processamento; chamar terminate() ao finalizar.
 */
export type ComissaoParseWorkerClient = {
  parse: (file: File, pasta: string) => Promise<ParseComissaoFileResult>
  terminate: () => void
}

export function createComissaoParseWorker(): ComissaoParseWorkerClient {
  const worker = new Worker(
    new URL('../workers/comissaoParse.worker.ts', import.meta.url),
    { type: 'module' }
  )

  const pending = new Map<string, Pending>()
  let seq = 0
  let terminated = false

  worker.onmessage = (event: MessageEvent<ComissaoParseWorkerResponse>) => {
    const { id, result } = event.data
    const entry = pending.get(id)
    if (!entry) return
    pending.delete(id)
    entry.resolve(result)
  }

  worker.onerror = (event) => {
    const message = event.message || 'Erro no worker de leitura de comissões.'
    for (const entry of pending.values()) {
      entry.reject(new Error(message))
    }
    pending.clear()
  }

  return {
    async parse(file: File, pasta: string): Promise<ParseComissaoFileResult> {
      if (terminated) {
        return { ok: false, message: 'Worker de leitura já foi encerrado.' }
      }

      const id = `parse-${++seq}`
      const buffer = await file.arrayBuffer()

      return new Promise<ParseComissaoFileResult>((resolve, reject) => {
        pending.set(id, { resolve, reject })
        const request: ComissaoParseWorkerRequest = {
          id,
          buffer,
          fileName: file.name,
          pasta,
        }
        worker.postMessage(request, [buffer])
      })
    },

    terminate() {
      if (terminated) return
      terminated = true
      for (const entry of pending.values()) {
        entry.reject(new Error('Worker de leitura encerrado.'))
      }
      pending.clear()
      worker.terminate()
    },
  }
}
